import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { Channel } from '../../shared/host';
import type {
  EmailDraft,
  EmailDraftMode,
  EmailSearchResult,
  EmailSearchWindow,
} from '../../integrations/email/types';
import type { WordDocumentContext } from '../../../integrations/tools/word/types';
import type { ApprovalRequest, ChatMessage, Pill, PlanItem, SSEEvent, WordComment, WordOutline } from '../types';
import { guardrailMessage } from '../guardrails';
import { GARBLED_REPLY_FALLBACK } from '../garbledReply';
import {
  emitPills,
  tryParseCalendarSources,
  tryParseDraft,
  tryParseEmailSources,
  tryParseListEmailsOutput,
  tryParseNbaSources,
  tryParseSearchResults,
} from '../../../integrations/tools/outlook';
import { runHandler } from '../../../integrations/tools';
import type { CapabilitySpan } from '../../telemetry';
import { outlineAwareLabel, parseReviewListFindings } from '../../../integrations/tools/word/utils/parseWordResponse';
import {
  applyWordPillsFill,
  declaresSynthesisablePill,
  extractFirstWordPillText,
  extractTableData,
  filterCalendarSourcesByMentions,
  declaresTextPill,
  isDegenerateTable,
  markPillsNeedingSelection,
  isNonInsertableOrFindings,
  isQuestionOrDecline,
  mergeSearchResultsById,
  WORD_TEXT_PILL_HANDLERS,
} from './useChatHelpers';
import {
  buildOutlookPills,
  resolvePersonaSkillKeys,
  toolOutputIsFailure,
  type OutlookSurface,
} from '../pills/outlookPills';
import { buildPowerPointPills } from '../pills/powerpointPills';
import type { ChatRoute } from './useChat';

/**
 * Per-turn SSE event handling, extracted from useChat's runChatTurn closure
 * (AIVA-5929). Each chat turn composes a fresh {@link TurnState} with the
 * hook-level {@link TurnDeps} via {@link createTurnEventHandler}; the returned
 * function is handed to streamChat / resumeChat as the onEvent callback.
 *
 * The v1 event cases (token, reasoning, tool_start/end, frontend_tool_call,
 * display_pill, paused, done, error) are ported verbatim; the chat-v2 cases
 * (plan_update, activity, approval_required, guardrail_flagged, action_pills)
 * follow the backend v2 stream semantics.
 */

/**
 * A `frontend_tool_call` queued from the current stream. The dispatch loop in
 * useChat drains the queue after the SSE reader returns and POSTs a single
 * /chat/resume per batch (the backend resolves every unresolved tool_call_id
 * in the checkpoint against the same result payload).
 */
export interface PendingToolCall {
  toolCallId: string;
  handler: string;
  args: Record<string, unknown>;
  timeoutMs: number;
}

/** Where a `handoff` event sends a turn v2 did not run. */
export type HandoffTarget = 'deckorate' | 'rephrase_slide';

/** Mutable per-turn stream state. Created fresh for every chat turn. */
export interface TurnState {
  /** Accumulated assistant response text (token events). */
  assistantContent: string;
  /** Accumulated reasoning text (reasoning events). */
  reasoningContent: string;
  /** Set by a `handoff` event: v2 ran nothing and the turn must be re-run
   *  elsewhere — on the DeckorateChat endpoint (AIVA-8792), or as the
   *  add-in's slide rewrite, SC-06 (AIVA-9184). Null otherwise. */
  handoffTarget: HandoffTarget | null;
  /** Skill key reported by the SSE `done` event. */
  doneSkillKey: string | undefined;
  /** Backend-declared tool whitelist from the `done` event. */
  doneAllowedTools: string[] | undefined;
  /** Backend's answer to "could this turn commit anything to the document?"
   *  undefined on older streams / the resume path — treated as unknown, never
   *  as false, so a missing flag cannot silently remove working pills. */
  doneCommittable: boolean | undefined;
  /** How much text the model had streamed at the moment pills fired, captured
   *  ONCE on the first token after them.
   *
   *  The re-stream probe must arm only when there was genuinely pre-pills text
   *  to re-stream. Reading `assistantContent.length > 0` live conflated that
   *  with "this emission has started": the first character of a brand-new
   *  post-pills emission made it non-empty, so the SECOND character arrived at
   *  a probe that then inserted a paragraph break behind it. A /format reply
   *  rendered as "R" on one line and "aising a purchase order" on the next
   *  (AIVA-7733). Null until the first post-pills token. */
  prePillsContentLength: number | null;
  /** Post-pills duplicate-stream probe buffer (see handleTokenChunk). */
  postPillsProbe: string;
  /** Verdict on post-pills tokens: still probing, accepted, or a re-stream. */
  postPillsDecision: 'probing' | 'accept' | 'suppress';
  /** Frontend tool calls awaiting local dispatch + /chat/resume. */
  pendingQueue: PendingToolCall[];
  /** Start ISOs of backend-attached book-slot pills (duplicate suppression). */
  backendBookSlotIsos: Set<string>;
  /** True once DP-05's candidate pills have been rendered from the search
   *  tool's own output, so a late pill_word_insert_email call is ignored
   *  instead of doubling the row. */
  pasteEmailPillsEmitted: boolean;
  /** True once the outline card rendered this turn. Lets a turn whose model
   *  never called pill_word_insert_outline still be given the Insert/Copy row —
   *  see emitWordOutlinePillsFallback. */
  wordOutlineRendered: boolean;
  /** Latest full plan snapshot from `plan_update` (each replaces the last). */
  plan: PlanItem[] | undefined;
  /** True once `guardrail_flagged` fired — suppresses later tokens and pills. */
  guardrailFlagged: boolean;
  /** True once `reply_replaced` fired (AIVA-8875): the reply leaked tool-call
   *  markup and was swapped for a clean message, so it offers no pills. */
  replyReplaced: boolean;
  /**
   * True once an `approval_required` parked a card this turn. Distinguishes the
   * two reasons a stream can end in `paused`: an approval ENDS the turn (the
   * user must decide), whereas a `frontend_tool_call` pause is resumed and
   * streams its text into the same message moments later. Only the former needs
   * an empty-bubble fallback.
   */
  approvalRequested: boolean;
  /** Content Safety categories captured from `guardrail_flagged`. */
  guardrailCategories: string[] | undefined;
  /** Flagged input sources captured from `guardrail_flagged` (AIVA-6560). */
  guardrailSources: string[] | undefined;
  /** Wall-clock ms of the first reasoning token this turn. */
  reasoningStartedAt: number | null;
  /** Wall-clock ms of the most recent reasoning token this turn. */
  reasoningEndedAt: number | null;
  /** Committed duration, stamped onto the message when the turn settles. */
  reasoningDurationMs: number | undefined;
  /**
   * The draft captured from `submit_email_draft`'s `tool_end` this turn, if any.
   *
   * Tier-1 evidence for the Outlook pill row: a draft here means the model
   * actually produced one rather than asking a question, AND it carries the
   * validated subject/to/cc that the streamed text alone does not.
   */
  capturedDraft: EmailDraft | null;
  /**
   * How many emails `list_emails_obo` returned this turn — `null` when it never
   * ran.
   *
   * Three states, not two, and the distinction is the whole point: an inbox-listing
   * skill that searched and found NOTHING has nothing for the user to act on, but a
   * turn where the tool never ran tells us nothing either way. The existing
   * `listEmailsHadResultsRef` cannot express that — it is a boolean defaulting to
   * `false`, so "empty inbox" and "never looked" are the same value. Suppressing
   * pills on that would silently drop them from any turn that answered without
   * listing.
   */
  listEmailsResultCount: number | null;
  /**
   * Tools that ran AND succeeded this turn, accumulated across every resume
   * stream the turn spans.
   *
   * This is why the check lives here rather than on the backend: a HITL turn is
   * three HTTP requests (approval pause, frontend-tool pause, final stream) and
   * the backend builds fresh per-request state, so it loses sight of a draft that
   * succeeded two requests ago. `useApprovalDecision` and the resume loop both
   * reuse ONE TurnState, so this set sees the whole turn.
   */
  succeededTools: Set<string>;
  /**
   * Tools that ran this turn and returned a failure, accumulated across every
   * resume stream — same reason as {@link succeededTools}.
   *
   * When tools ran and none succeeded, the reply is an apology rather than an
   * answer, and must not get action pills offering to place it in an email.
   */
  failedTools: Set<string>;
  /**
   * Whether this turn produced something that could be placed into an email.
   *
   * Sent by the backend on `done`. Defaults true so a backend that does not send
   * it behaves as today — see the `committable` flag in
   * docs/outlook-action-pills-design.md.
   */
  committable: boolean;
}

/**
 * The Teams booking tool's status when it held a booking back to ask about
 * attendees outside the organisation (`_STATUS_EXTERNAL_NEEDS_CONFIRM` in the AI
 * backend's `meeting_validators.py`).
 */
const EXTERNAL_ATTENDEES_NEED_CONFIRMATION = 'external_recipients_require_confirmation';

/**
 * How many emails a `list_emails_obo` result contains, or null if it is not a
 * JSON array at all.
 *
 * Deliberately separate from `tryParseListEmailsOutput`, which folds "empty",
 * "unparseable" and "wrong shape" into a single null. The pill row has to tell an
 * empty inbox from a tool that never ran, so it needs the zero.
 */
function countListedEmails(output: string): number | null {
  try {
    const parsed = JSON.parse(output) as unknown;
    return Array.isArray(parsed) ? parsed.length : null;
  } catch {
    return null;
  }
}

export function createTurnState(): TurnState {
  return {
    assistantContent: '',
    reasoningContent: '',
    handoffTarget: null,
    doneSkillKey: undefined,
    doneAllowedTools: undefined,
    doneCommittable: undefined,
    prePillsContentLength: null,
    postPillsProbe: '',
    postPillsDecision: 'probing',
    pendingQueue: [],
    backendBookSlotIsos: new Set<string>(),
    pasteEmailPillsEmitted: false,
    wordOutlineRendered: false,
    plan: undefined,
    guardrailFlagged: false,
    replyReplaced: false,
    approvalRequested: false,
    guardrailCategories: undefined,
    guardrailSources: undefined,
    reasoningStartedAt: null,
    reasoningEndedAt: null,
    reasoningDurationMs: undefined,
    capturedDraft: null,
    listEmailsResultCount: null,
    committable: true,
    succeededTools: new Set<string>(),
    failedTools: new Set<string>(),
  };
}

/**
 * Hook-level collaborators the per-turn handler needs. All refs are the
 * live useChat refs, so mutations made here are visible to the dispatch
 * loop and to the render-time fallbacks.
 */
export interface TurnDeps {
  /** The Office host. A turn only exists once the host-null guard passed. */
  host: Channel;
  wordDoc: WordDocumentContext | null;
  /** Id of the assistant message this turn streams into. */
  assistantMsgId: string;
  /** Timestamp stamped on the turn's messages. */
  turnTimestamp: number;
  /** Forced skill for this turn (slash command / pill click), if any. */
  forcedSkillKey?: string;
  /** Word autoApply flag from the invoking slash command, if any. */
  autoApply?: boolean;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setActiveTool: (tool: string | null) => void;
  setLatestDraft: (draft: EmailDraft) => void;
  /** Record external recipients the user has approved (AIVA-7482) so later turns
   *  carry them and the guard stops re-asking about the same people. */
  recordConfirmedExternals: (addresses: string[]) => void;
  setLatestSearchResults: Dispatch<SetStateAction<EmailSearchResult[]>>;
  pillsEmittedRef: MutableRefObject<boolean>;
  listEmailsHadResultsRef: MutableRefObject<boolean>;
  /** First pill text this turn, for an empty reply. Word's, and PowerPoint's since AIVA-8972. */
  lastWordPillTextRef: MutableRefObject<string>;
  composeDraftIdRef: MutableRefObject<string>;
  latestDraftModeRef: MutableRefObject<EmailDraftMode | null>;
  /** Sanitised backend progress line (v2 `activity`); null clears it. */
  setActivityLine: (label: string | null) => void;
  /** AIVA-8194: the rule keys the latest word.docreview.scan was asked to
   *  sweep, or null when it was given none — the judge step's progress lines
   *  name only these checks instead of every judgment rule. */
  setDocReviewScope?: (rules: string[] | null) => void;
  /** Called when a terminal event (`done`/`paused`) carries a thread id. */
  onThreadIdResolved: (threadId: string, reset: boolean) => void;
  /** Flags that word.docreview.anchor_findings has dispatched this session
   *  (AIVA-7506), so the inline doc-review card starts fetching findings. */
  markDocReviewReady: () => void;
  /** The live thread id (AIVA-7506) — word.docreview.scan's handler needs
   *  this to POST its deterministic findings to the bulk-create endpoint. A
   *  ref, not a snapshot value, so the handler reads whatever id is current
   *  at dispatch time, same reasoning as every other ref here. */
  threadIdRef: MutableRefObject<string | null>;
  /** Auth context (AIVA-7506) threaded into `runHandler`'s optional `ctx`
   *  param for handlers that call a REST endpoint directly — mirrors
   *  Deckorate's own use of `HandlerContext` for its content backend calls. */
  apiToken: string | null;
  refresh?: () => Promise<string | null>;
  /** The turn's abort signal (AIVA-8199): Stop also cancels a running frontend tool. */
  signal?: AbortSignal;
  /** Which Outlook surface is open — read, compose, or null for no item.
   *  Half of the key into the Outlook pill table; see chat/pills/outlookPills.ts. */
  outlookSurface?: OutlookSurface | null;
  /** Which backend this turn went to (AIVA-8744). PowerPoint builds its action
   *  bar only for the general route — a Deckorate turn has its own UI. Absent
   *  means general, as it does for every Word and Outlook turn. */
  route?: ChatRoute;
  /** PowerPoint (SC-03, AIVA-6754): a shape with text was selected when the turn
   *  was sent, so the action bar offers "Replace selection" in place of "Insert
   *  into slide". Set from the slide read, which runs after these deps are made. */
  pptTextShapeSelected?: boolean;
  capabilitySpan?: CapabilitySpan;
  /** The user's prompt for this turn (AIVA-7026 / spec §2). The Outlook action
   *  bar reshapes for reply / new-email / compose / meeting intent read from
   *  what the user TYPED, because the prompt is stronger evidence than which
   *  skill ran. Absent on resume/rehydration turns, where buildOutlookPills
   *  falls back to the skill-keyed bar. */
  promptText?: string;
}

/** Map one aggregated `action_pills` payload entry to the renderable union. */
function mapAggregatePill(raw: Record<string, unknown>): Pill | null {
  const label = typeof raw.label === 'string' ? raw.label : null;
  if (!label) return null;
  if (typeof raw.handler === 'string') {
    return {
      type: 'office_action',
      label,
      handler: raw.handler,
      args: (raw.args as Record<string, unknown> | undefined) ?? {},
    };
  }
  if (typeof raw.message === 'string') {
    return raw.hidden === true
      ? { type: 'chat_intent', label, message: raw.message, hidden: true }
      : { type: 'chat_intent', label, message: raw.message };
  }
  return null;
}

/**
 * Whether the stream-time Word pill fallback may synthesise placement pills.
 *
 * What the skill DECLARED is the authority. This replaced a `word_`/`document_`
 * prefix test plus three hand-written skill-key exclusions — see declaresTextPill
 * for why that list was wrong in both directions. The prefix test survives only
 * for streams that send no allowed_tools (older builds, the resume path), where
 * nothing better exists.
 *
 * Both branches are required, not just the text-pill one: a skill can declare a
 * synthesisable pill without declaring a TEXT placement (word_table), and one
 * that declares only its own pill (word_paste_email) must get nothing — a
 * synthesised "Insert at cursor" under its "no matches" reply would paste that
 * guidance into the document.
 */
function wordFallbackIsAuthorised(allowedTools: string[] | undefined, skillKey: string | undefined): boolean {
  if (allowedTools) {
    return declaresSynthesisablePill(allowedTools) && declaresTextPill(allowedTools);
  }
  return (
    !!skillKey &&
    skillKey !== 'word_general' &&
    (skillKey.startsWith('word_') || skillKey.startsWith('document_'))
  );
}

/**
 * Creates the per-turn SSE event handler. `state` must be a fresh
 * {@link createTurnState} result — the closure mutates it as events arrive.
 */
/**
 * Join a fresh assistant emission onto the text already in the bubble.
 *
 * Post-pills text is a NEW assistant message, not a continuation of the last
 * token, so a raw `+=` ran the two straight together. An /improve turn that
 * streamed one line, fired its pills, then streamed a second line rendered as
 * "...leave tomorrowRequesting leave for tomorrow" — no space, no break.
 *
 * Nothing in the SSE contract marks a message boundary (there is no such event
 * type in `StreamEventType`), so it has to be inferred. The probe append is the
 * one place that is always at a boundary: within a single message the tokens go
 * through the plain `+=` path below and keep concatenating verbatim.
 *
 * A blank line rather than a space, because the two emissions are separate
 * paragraphs — a space would glue a heading to the sentence after it.
 *
 * EXCEPT when the tool call landed mid-word. The boundary is inferred, and the
 * inference is wrong whenever the model starts a sentence, calls a tool, then
 * finishes the sentence: /outline streamed "I", fired its pill, then continued
 * "'ve inserted a structured outline…", and the break rendered a lone "I" above
 * a paragraph opening with an apostrophe.
 *
 * Only ONE continuation is safe to detect: a contraction suffix. No paragraph
 * begins with "'ve" or "'s", so concatenating those verbatim cannot glue two
 * genuine emissions together.
 *
 * A broader "incoming starts lower-case" rule was tried and reverted — it fires
 * on any real boundary whose second emission opens with a lower-case word, and
 * joins with NO separator: "I've added a title" + "and three sections follow"
 * rendered "…a titleand three sections follow". That is worse than the stray
 * break it was meant to fix, and the bubble text is what Insert at cursor
 * places (§4.1 — "so the action buttons can place the reply verbatim").
 */
function joinEmission(existing: string, incoming: string): string {
  if (existing.length === 0 || incoming.length === 0) return existing + incoming;
  const alreadySeparated = /\s$/.test(existing) || /^\s/.test(incoming);
  if (alreadySeparated) return existing + incoming;
  const continuesWord = /[\p{L}\p{N}]$/u.test(existing) && /^['’]\p{Ll}/u.test(incoming);
  return continuesWord ? existing + incoming : `${existing}\n\n${incoming}`;
}

export function createTurnEventHandler(state: TurnState, deps: TurnDeps): (event: SSEEvent) => void {
  const { host, wordDoc } = deps;

  // Every write below MUST target THIS turn's message by id, never "the last
  // array slot" or "the last assistant message" — a concurrent turn (e.g. the
  // user resolves a second approval card before this turn's stream has fully
  // drained) appends its own message afterwards, so positional writes land in
  // the WRONG turn's slot. That silently overwrote a settled message with a
  // stale id and orphaned this turn's content, which is what made two
  // genuinely separate responses appear to merge into one bubble.
  const findAssistantIndex = (list: readonly ChatMessage[]): number =>
    list.findIndex((m) => m.id === deps.assistantMsgId);

  const updateAssistant = () => {
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      const last = updated[idx];
      updated[idx] = {
        id: deps.assistantMsgId,
        role: 'assistant',
        content: state.assistantContent,
        reasoning: state.reasoningContent || undefined,
        // Committed once when the turn settles (done/paused) — before that
        // the previous message's value (normally undefined) carries through.
        reasoningDurationMs: state.reasoningDurationMs ?? last?.reasoningDurationMs,
        // Preserve search results set by a prior tool_end event — without
        // this, incoming tokens would overwrite the searchResults field.
        searchResults: last?.searchResults,
        searchQuerySummary: last?.searchQuerySummary,
        searchWindow: last?.searchWindow,
        calendarSources: last?.calendarSources,
        // Same reason as searchResults above: word.list_comments attaches this
        // during the frontend-tool interrupt, and every token that follows would
        // otherwise rebuild the message without it — so the count sentence
        // rendered while the comment rows silently vanished (RV-02).
        wordComments: last?.wordComments,
        // Same reason as wordComments: word.get_document_structure attaches the
        // outline during the frontend-tool interrupt, and the sentence the model
        // streams afterwards would otherwise rebuild the message without it —
        // leaving the summary line with no outline under it.
        wordOutline: last?.wordOutline,
        // A guardrail-flagged turn renders as a static notice: pills and the
        // live plan are stripped so no action UI survives on the message.
        actionPills: state.guardrailFlagged || state.replyReplaced ? undefined : last?.actionPills,
        plan: state.guardrailFlagged ? undefined : (state.plan ?? last?.plan),
        approval: last?.approval,
        // Same trap as wordComments/approval above: the plan card is parked by
        // its own event, and every later rebuild — the `done` event's included —
        // would drop it. Guardrail-stripped alongside the other action UI, since
        // a flagged turn must offer no way to start work.
        multipartPlan: state.guardrailFlagged ? undefined : last?.multipartPlan,
        // DI-01: stamped on the placeholder before the first token, so every rebuild carries it.
        deckSummary: state.guardrailFlagged ? undefined : last?.deckSummary,
        guardrailCategories: state.guardrailCategories ?? last?.guardrailCategories,
        guardrailSources: state.guardrailSources ?? last?.guardrailSources,
        // Set by the Teams booking tool_end; the question streams in
        // after it, and each token's rebuild would otherwise drop the flag.
        externalAttendeeQuestion: last?.externalAttendeeQuestion,
        // Snapshot the backend-declared allowedTools so buildWordDocPills
        // at render time can gate pill injection strictly (e.g. /grammar
        // must not spawn Insert/Append pills it never declared).
        allowedTools: state.doneAllowedTools,
        committable: state.doneCommittable,
        // Confirmed by the `done` event once the turn completes; falls back to
        // the placeholder's forcedSkillKey (set at turn start, see runChatTurn)
        // so a stopped/aborted stream still leaves the message queryable by
        // regenerate() for its selection requirements (AIVA-7079).
        skillKey: state.doneSkillKey ?? last?.skillKey,
        // Carried forward for the same reason as skillKey above: the frozen
        // selection is set on the placeholder at turn start, and this rebuild
        // would otherwise drop it - leaving regenerate unable to tell that a
        // stopped turn had run against selected text (AIVA-7571).
        sentSelection: last?.sentSelection,
        timestamp: last?.timestamp ?? deps.turnTimestamp,
      };
      return updated;
    });
  };

  const replaceLastMessageSearchResults = (
    results: EmailSearchResult[],
    querySummary: string,
    searchWindow?: EmailSearchWindow
  ) => {
    deps.setLatestSearchResults(results);
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx]!,
        searchResults: results,
        searchQuerySummary: querySummary,
        // Spread-conditional, not `searchWindow: searchWindow`: a caller with
        // nothing to disclose (fetch_emails) must not wipe a block an earlier
        // submit on the same turn already set.
        ...(searchWindow && { searchWindow }),
      };
      return updated;
    });
  };

  const handleSubmitEmailDraft = (output: string) => {
    const draft = tryParseDraft(output);
    if (draft) {
      deps.setLatestDraft(draft);
      deps.latestDraftModeRef.current = draft.mode ?? null;
      // Kept on the turn as well as in hook state: the Outlook pill row is built
      // at `done` and needs THIS turn's draft, whereas `latestDraft` persists
      // across turns and would let a stale draft dress up a later reply.
      state.capturedDraft = draft;
      // A parseable draft IS the success signal — every early return in the
      // submit tool is an error envelope, not a draft.
      state.succeededTools.add('submit_email_draft');
    }
  };

  const handleListEmailsObo = (output: string) => {
    // Count FIRST, from a direct parse, and before the early return below.
    //
    // `tryParseListEmailsOutput` returns null for an empty array as readily as for
    // unparseable output, so reading the count from it leaves us at "never ran" on
    // exactly the turn we need to detect — an empty inbox. It also returns null for
    // a well-formed array whose first item has no `id`, which is a shape problem,
    // not an empty result.
    //
    // Accumulated rather than assigned: a turn can call this tool several times
    // (inbox, then sent items, then a date-scoped retry). If ANY call returned
    // emails the turn found something, so only an all-zero turn totals zero.
    const listed = countListedEmails(output);
    if (listed !== null) {
      state.listEmailsResultCount = (state.listEmailsResultCount ?? 0) + listed;
    }

    const newResults = tryParseListEmailsOutput(output);
    if (!newResults) return;
    deps.listEmailsHadResultsRef.current = newResults.length > 0;
    deps.setLatestSearchResults((prev) => mergeSearchResultsById(prev, newResults));
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      const last = updated[idx]!;
      updated[idx] = {
        ...last,
        searchResults: mergeSearchResultsById(last.searchResults ?? [], newResults),
        searchQuerySummary: last.searchQuerySummary ?? 'Related emails',
      };
      return updated;
    });
  };

  const handleFetchCalendarEvents = (output: string) => {
    const sources = tryParseCalendarSources(output);
    if (!sources) return;
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx]!, calendarSources: sources };
      return updated;
    });
  };

  const handleCreateDraftToolEnd = (event: SSEEvent, output: string) => {
    // Capture the authoritative draft_id AND deterministically emit the
    // open/reply pill ourselves. We do NOT rely on the LLM calling
    // pill_office_open_attachment_draft: on the compose path it often skips
    // that step (AIVA-5594), leaving the user with a draft they can't open.
    // Emitting here guarantees the pill, with the correct id straight from
    // the tool result (no LLM transcription). The pill sink dedupes if the
    // LLM happens to emit one too.
    try {
      const parsed = JSON.parse(output) as Record<string, unknown>;
      if (typeof parsed.draft_id === 'string' && parsed.draft_id) {
        deps.composeDraftIdRef.current = parsed.draft_id;
        // A returned draft_id is this tool's success signal, which the Outlook
        // pill row needs to confirm the attachment skills produced a draft.
        if (typeof event.data.tool === 'string') state.succeededTools.add(event.data.tool);
        const isReply = event.data.tool === 'create_reply_draft';
        const mode: EmailDraftMode = isReply ? 'reply' : 'compose';
        // ATT-1: register the draft this tool just created as the session's
        // latest. Without this, `latestDraft` keeps whatever submit_email_draft
        // last produced — possibly from an earlier, unrelated task in the same
        // chat — and a later "make it warmer" retones THAT stale draft instead,
        // silently dropping the attachment (body byte-identical, no tone
        // change, no file).
        //
        // subject/body/to/cc are deliberately left blank: this tool's result
        // carries neither (they live in the call args, and the streamed
        // tool_start event does not reliably expose full args — see
        // agent_base._emit_chunk_tool_calls, which only forwards the first,
        // usually-empty argument chunk). Sending an EMPTY body is exactly what
        // makes the backend's `_draft_or_rebuilt` rebuild the full draft from
        // the checkpoint transcript (subject/body/recipients AND attachments)
        // instead of trusting a stale client-sent one.
        deps.setLatestDraft({
          subject: '',
          body: '',
          to: [],
          cc: [],
          mode,
          draft_id: parsed.draft_id,
          threaded: typeof parsed.threaded === 'boolean' ? parsed.threaded : undefined,
          attachments:
            typeof parsed.attachments === 'string' && parsed.attachments
              ? parsed.attachments
                  .split(',')
                  .map((name) => name.trim())
                  .filter(Boolean)
              : [],
        });
        deps.latestDraftModeRef.current = mode;
        emitPills([
          {
            type: 'office_action',
            label: isReply ? 'Reply' : 'Open',
            handler: 'attachment.open_draft',
            args: { draft_id: parsed.draft_id },
          },
        ]);
      }
    } catch {
      // non-JSON output — draft creation failed, leave ref empty
    }
  };

  /**
   * DP-05 — render the candidate pills from the SEARCH tool's own output.
   *
   * The skill prompt used to ask the model to call pill_word_insert_email and
   * pass the `candidates` array through "COMPLETELY UNCHANGED". That asks it to
   * reproduce every candidate's full sanitised body as tool-call arguments —
   * the backend caps a response's bodies in aggregate, but that is still tens of
   * thousands of characters of verbatim JSON. On a real thread the model simply
   * stops: in the AIVA-6957
   * dev trace it wrote "Please click one of the options" and never made the
   * call, so the row was empty and the search looked broken.
   *
   * The add-in already has the candidates — the same `tool_end` event carries
   * them — so nothing needs to round-trip through the model at all. Dispatching
   * the existing ui.pill.word.insert_email handler keeps ONE pill-construction
   * site (handlers.ts) rather than rebuilding the row here.
   *
   * It also closes a correctness hole that had nothing to do with size: a model
   * that paraphrased a body while "passing it through" would paste altered text
   * into a client document, and nothing downstream could tell.
   */
  const handleWordPasteEmailToolEnd = (output: unknown) => {
    if (host !== 'word' || state.pasteEmailPillsEmitted) return;
    if (typeof output !== 'string') return;
    let parsed: { status?: unknown; candidates?: unknown };
    try {
      parsed = JSON.parse(output) as { status?: unknown; candidates?: unknown };
    } catch {
      return; // not JSON — the tool's error paths are prose, and carry no pills
    }
    if (parsed.status !== 'candidates' || !Array.isArray(parsed.candidates)) return;
    if (parsed.candidates.length === 0) return;
    state.pasteEmailPillsEmitted = true;
    deps.pillsEmittedRef.current = true;
    void runHandler(host, 'ui.pill.word.insert_email', { candidates: parsed.candidates }, 5000);
  };

  /** Mark this turn's reply as the external-attendee question. */
  const markExternalAttendeeQuestion = () => {
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx]!, externalAttendeeQuestion: true };
      return updated;
    });
  };

  /**
   * A booking went ahead with people outside the organisation — remember who.
   *
   * AIVA-7482. The backend echoes `confirmed_external` on success only, and the
   * add-in re-sends it on later turns. Without this, every follow-up turn re-asks
   * about recipients the user has already approved, because the only other
   * evidence is a flag the model has to remember to set again. Reported as
   * "repeated confirmations from LLM" on the ticket.
   *
   * Success only, by construction: the field is absent on the refusal envelope,
   * so a question the user never answered records nothing.
   */
  const handleTeamsMeetingToolEnd = (output: string) => {
    try {
      const parsed = JSON.parse(output) as { confirmed_external?: unknown; status?: unknown };
      // The booking was held back to ask about outsiders, so the reply
      // that follows is that question — mark it to render as a warning. Keyed on
      // the backend's machine status, never on the model's wording.
      if (parsed.status === EXTERNAL_ATTENDEES_NEED_CONFIRMATION) markExternalAttendeeQuestion();
      const approved = Array.isArray(parsed.confirmed_external)
        ? parsed.confirmed_external.filter((a): a is string => typeof a === 'string' && a.trim().length > 0)
        : [];
      if (approved.length > 0) deps.recordConfirmedExternals(approved);
    } catch {
      // Not JSON (or a shape we don't recognise) — nothing to record. The booking
      // itself is unaffected; this only ever adds recall.
    }
  };

  const handleToolEnd = (event: SSEEvent) => {
    const output = event.data.output;
    // Record the outcome of EVERY tool, before the per-tool switch below only
    // looks at the handful it renders. A turn whose tools all failed produced an
    // apology, not an answer, and must not get action pills (see
    // buildOutlookPills). Successes are recorded per-tool in their own handlers,
    // where "success" has a precise meaning.
    if (typeof event.data.tool === 'string' && toolOutputIsFailure(output)) {
      state.failedTools.add(event.data.tool);
    }
    if (!output) {
      deps.setActiveTool(null);
      return;
    }
    switch (event.data.tool) {
      case 'word_paste_email':
        handleWordPasteEmailToolEnd(output);
        break;
      case 'submit_email_draft':
        handleSubmitEmailDraft(output);
        break;
      case 'create_compose_draft':
      case 'create_reply_draft':
        handleCreateDraftToolEnd(event, output);
        break;
      case 'list_emails_obo':
        handleListEmailsObo(output);
        break;
      case 'submit_email_search_results': {
        const parsed = tryParseSearchResults(output);
        if (parsed) replaceLastMessageSearchResults(parsed.results, parsed.querySummary, parsed.searchWindow);
        break;
      }
      case 'fetch_emails': {
        const parsed = tryParseEmailSources(output);
        if (parsed) replaceLastMessageSearchResults(parsed.results, parsed.querySummary);
        break;
      }
      case 'fetch_calendar_events':
        handleFetchCalendarEvents(output);
        break;
      case 'manage_calendar':
        // update_event / respond_to_event embed the same
        // __CALENDAR_SOURCES__ marker as fetch_calendar_events, so the
        // updated event renders as a calendar card alongside the
        // confirmation text.
        handleFetchCalendarEvents(output);
        break;
      case 'submit_teams_meeting_draft':
        handleTeamsMeetingToolEnd(output);
        break;
      case 'produce_agenda':
        // On a successful agenda save, produce_agenda emits the same
        // __CALENDAR_SOURCES__ marker so the user gets a clickable card to
        // verify the saved agenda. Error / meeting_not_saved outputs carry
        // no marker, so tryParseCalendarSources returns null and this is a
        // no-op. Routes through the same parser, so the end time is
        // formatted to friendly local time too (AIVA-5166).
        handleFetchCalendarEvents(output);
        break;
      case 'get_nbas': {
        const parsed = tryParseNbaSources(output);
        if (parsed) replaceLastMessageSearchResults(parsed.results, parsed.querySummary);
        break;
      }
    }
    deps.setActiveTool(null);
  };

  const handleTokenChunk = (chunk: string) => {
    // Tokens that arrive AFTER pills fired AND after the LLM already
    // streamed pre-pills text need a re-stream check. Probe the first
    // ~40 chars of post-pills content; if those characters already
    // exist as a substring of the pre-pills content, the LLM is
    // re-streaming the same response — suppress until end of turn.
    // Snapshot the pre-pills length on the FIRST token after pills, while
    // `assistantContent` still holds only what preceded them. Read live it
    // would grow with this very emission -- see prePillsContentLength.
    if (deps.pillsEmittedRef.current && state.prePillsContentLength === null) {
      state.prePillsContentLength = state.assistantContent.length;
    }
    const isPostPills = deps.pillsEmittedRef.current && (state.prePillsContentLength ?? 0) > 0;
    if (!isPostPills) {
      state.assistantContent += chunk;
      updateAssistant();
      return;
    }
    if (state.postPillsDecision === 'suppress') return;
    if (state.postPillsDecision === 'probing') {
      state.postPillsProbe += chunk;
      if (state.postPillsProbe.length < 40) return;
      const probeNorm = state.postPillsProbe.trim().replace(/\s+/g, ' ').toLowerCase();
      const existingNorm = state.assistantContent.replace(/\s+/g, ' ').toLowerCase();
      if (probeNorm.length > 0 && existingNorm.includes(probeNorm.slice(0, 30))) {
        state.postPillsDecision = 'suppress';
        return;
      }
      state.postPillsDecision = 'accept';
      state.assistantContent = joinEmission(state.assistantContent, state.postPillsProbe);
      state.postPillsProbe = '';
      updateAssistant();
      return;
    }
    state.assistantContent += chunk;
    updateAssistant();
  };

  const flushProbeIfPending = () => {
    // Flush any probe content that never reached the 40-char threshold.
    // Without this, short post-pills responses (under ~40 chars) get
    // stuck in the probe buffer and never render — you'd see only the
    // pre-pills text in the bubble even though the LLM said more.
    if (state.postPillsProbe.length === 0 || state.postPillsDecision !== 'probing') return;
    const probeNorm = state.postPillsProbe.trim().replace(/\s+/g, ' ').toLowerCase();
    const existingNorm = state.assistantContent.replace(/\s+/g, ' ').toLowerCase();
    const isDuplicate =
      probeNorm.length > 0 &&
      existingNorm.length >= probeNorm.length &&
      existingNorm.includes(probeNorm.slice(0, Math.min(30, probeNorm.length)));
    if (!isDuplicate) {
      state.assistantContent = joinEmission(state.assistantContent, state.postPillsProbe);
      updateAssistant();
    }
    state.postPillsProbe = '';
    state.postPillsDecision = 'accept';
  };

  const applyEmptyBubbleFallback = () => {
    // Safety net: an empty assistant bubble below a row of pills looks
    // broken. Fall back to a generic confirmation tied to the latest
    // draft so the bubble has SOMETHING to show.
    if (state.assistantContent.length !== 0 || !deps.pillsEmittedRef.current) return;
    if (deps.latestDraftModeRef.current === 'compose') {
      state.assistantContent = 'Done — your email draft is ready. Open it in Outlook to review.';
    } else if (deps.latestDraftModeRef.current === 'reply') {
      state.assistantContent = 'Done — your reply draft is ready. Click Reply to open it in Outlook.';
    } else if (host === 'word' && state.doneSkillKey === 'word_passive') {
      state.assistantContent =
        "Passive voice phrases have been highlighted in yellow. Click 'Clear highlights' to remove them.";
    } else if (host === 'word' && state.doneSkillKey === 'word_clarity') {
      state.assistantContent =
        "Clarity issues have been highlighted in cyan. Click 'Clear highlights' to remove them.";
    } else if (host === 'word') {
      state.assistantContent =
        deps.lastWordPillTextRef.current ||
        'Ready — click a button below to apply the changes to your document.';
    } else if (host === 'powerpoint' && deps.lastWordPillTextRef.current) {
      // AIVA-8972: the model sent its content only as a pill_ppt_* argument.
      // Runs before emitPowerPointActionPills, so the bar checks this text too.
      state.assistantContent = deps.lastWordPillTextRef.current;
    } else if ((state.doneSkillKey ?? deps.forcedSkillKey) === 'email_assistant') {
      // Catch-up: the Conversational-tier model sometimes emits the refine
      // pill but no prose. Give a meaningful line deterministically instead
      // of a bare "Done." — distinguishing the empty inbox from a results turn.
      state.assistantContent = deps.listEmailsHadResultsRef.current
        ? "Here's what needs your attention — see the emails below."
        : 'No unread mail needs your attention right now.';
    } else {
      state.assistantContent = 'Done.';
    }
    updateAssistant();
  };

  /**
   * Highlight the phrases the findings list names (AIVA-7722).
   *
   * The review skills are required to call `word_highlight_text` once per
   * finding and repeatedly do not: the user reads a correct list, sees the
   * Apply review card, and the document shows no yellow at all — so there is
   * nothing to find the flagged text BY until they click Apply.
   *
   * `emitWordActionPillsFallback` below cannot cover it: that path is gated on
   * `declaresTextPill`, and the review skills declare no text pill, so it
   * returns immediately. This runs from the same findings list the review bar
   * is recovered from, so the yellow and the card always name the same phrases.
   *
   * Idempotent on purpose, and NOT gated on `pillsEmittedRef`: a model that
   * emitted its pill may still have skipped the highlights, and re-highlighting
   * a phrase it did colour sets the same colour again. Fire-and-forget — a
   * highlight that misses is a cosmetic loss, and must never delay or fail the
   * turn.
   */
  const highlightReviewFindings = async () => {
    if (host !== 'word') return;
    // The backend said this turn places nothing in the document; a highlight is
    // a document write, so the same answer applies.
    if (state.doneCommittable === false) return;
    const findings = parseReviewListFindings(state.assistantContent);
    if (findings.length === 0) return;

    // Sequential and awaited, not fire-and-forget: the Clear highlights pill
    // below can only claim ids that already exist, so the colouring has to have
    // finished before it is offered.
    let applied = 0;
    for (const finding of findings) {
      try {
        // runHandler wraps the handler's return: { ok, data }, NOT the raw
        // value. Reading `count` off the envelope instead of off `data` made it
        // always undefined, so `applied` stayed 0 and the Clear highlights pill
        // below was skipped on every turn — the highlights landed and the button
        // never appeared.
        const result = (await runHandler( // NOSONAR highlights are applied one at a time in document order
          host,
          'word.highlight_text',
          { phrase: finding.matchText, color: 'Yellow' },
          5000,
        )) as { ok?: boolean; data?: { count?: unknown } } | undefined;
        const count = typeof result?.data?.count === 'number' ? result.data.count : 0;
        if (result?.ok && count > 0) applied += 1;
      } catch {
        // A phrase that will not anchor is a cosmetic loss. Keep going: the
        // remaining findings are independent, and the turn must not fail over a
        // highlight.
      }
    }
    if (applied === 0) return;

    // Whoever applies the highlights owns the way to remove them. The model's
    // own pill_word_clear_highlights call — if it made one — was declined
    // upstream because nothing was highlighted YET, so the button has to be
    // offered here instead.
    //
    // Via the existing pill handler rather than emitPills directly: that handler
    // claims exactly the ids created since the turn began, which is what stops
    // this button clearing a different response's highlights (AIVA-7725).
    try {
      await runHandler(host, 'ui.pill.word.clear_highlights', {}, 5000);
    } catch {
      // No button is a smaller loss than a failed turn.
    }
  };

  /**
   * /outline's safety net, and the only Word skill that had none.
   *
   * Every other skill's missing-pill fallback goes through
   * wordFallbackIsAuthorised, which requires a declared TEXT placement pill.
   * /outline declares none — deliberately, since "Insert at cursor" on the reply
   * would paste the sentence "The document already has a structured outline"
   * into the document. So when the model wrote its hand-off sentence and skipped
   * pill_word_insert_outline, the turn rendered the card and nothing to click.
   *
   * Calls the pill's own handler rather than rebuilding the row here, so the
   * fallback cannot drift from the normal path: same sections, same count, same
   * Copy. The sweep this turn already took is still cached, so it is not a
   * second read.
   */
  const emitWordOutlinePillsFallback = async () => {
    if (host !== 'word' || deps.pillsEmittedRef.current) return;
    // A flagged turn offers no action UI, matching the pill stripping elsewhere.
    if (state.guardrailFlagged) return;
    // Only where the card actually rendered: that is what makes the outline the
    // add-in's to offer, rather than something inferred from the prose.
    if (!state.wordOutlineRendered) return;
    // Never synthesise a pill a skill did not declare.
    if (state.doneAllowedTools && !state.doneAllowedTools.includes('pill_word_insert_outline')) return;
    try {
      // 'all' because no depth was asked for — the same default the prompt gives.
      await runHandler(host, 'ui.pill.word.insert_outline', { depth: 'all' }, 15000);
    } catch {
      // No button is a smaller loss than a failed turn.
    }
  };

  const emitWordActionPillsFallback = () => {
    // Word fallback: if the LLM generated content but never called any ui.pill.* tool,
    // auto-emit the standard 3 Word action pills so the user can still apply the text.
    // Only fires for substantive responses (>50 chars) to avoid pills on short answers.
    if (host !== 'word' || state.assistantContent.length <= 50 || deps.pillsEmittedRef.current) return;
    // Error / system / guidance responses carry no insertable content — never
    // attach document pills to them (AIVA-5715, AIVA-5657).
    //
    // Findings reports are the same case and were being missed here: /terms
    // answering "## No Inconsistencies Found" is not insertable text, but this
    // fallback only checked isNonInsertableResponse and so emitted Replace /
    // Insert / Append anyway — clicking Replace pasted the report over the
    // user's selection. The skill-key exclusions below can't cover it either
    // (they list word_passive / word_clarity / word_table by hand, and
    // word_terms isn't among them), so gate on the CONTENT instead.
    // The backend said this turn has nothing to place. Authoritative, and
    // checked BEFORE the prose heuristic below — that is the point of this
    // change: the add-in stops inferring intent by pattern-matching the reply.
    // The heuristic stays as the fallback for streams that send no flag.
    if (state.doneCommittable === false) return;
    if (isNonInsertableOrFindings(state.assistantContent) || isQuestionOrDecline(state.assistantContent)) return;
    // Fall back to forcedSkillKey when the backend omits skill_key from the done event.
    const effectiveSkillKey = state.doneSkillKey ?? deps.forcedSkillKey;
    // The declared toolset decides whether placement pills belong here at all —
    // see wordFallbackIsAuthorised for what it replaced and why.
    if (!wordFallbackIsAuthorised(state.doneAllowedTools, effectiveSkillKey)) return;
    // Kept from the old list, and NOT a skill-identity rule: /format with nothing
    // selected returns a "select text first" nudge, which is not insertable whatever
    // the skill declares. Slice 3 folds this into the table's context axis.
    if (effectiveSkillKey === 'word_format_router' && !wordDoc?.hasSelection) return;
    // Keep "- " bullet markers and "# " heading markers — the Word insertion
    // service (parseWithHeadings) converts them to List Bullet / Heading styles.
    // Only strip inline formatting that Word doesn't need.
    const plainText = state.assistantContent
      .replace(/\*\*(.*?)\*\*/gs, '$1')
      .replace(/\*(.*?)\*/gs, '$1')
      .replace(/`[^`]*`/g, '')
      .trim();
    deps.pillsEmittedRef.current = true;
    if (deps.autoApply && wordDoc?.hasSelection) {
      void runHandler(host, 'word.replace_selection', { text: plainText }, 5000);
      return;
    }
    // Labels go through outlineAwareLabel so an outline reply that reached the
    // fallback (model never called the pill tools) is labelled the same way the
    // pill-tool path labels it — "Insert outline (7)" rather than a bare
    // "Insert at cursor".
    emitPills([
      ...(wordDoc?.hasSelection
        ? [
            {
              type: 'office_action' as const,
              label: outlineAwareLabel('Replace selection', plainText),
              handler: 'word.replace_selection',
              args: { text: plainText },
            },
          ]
        : []),
      {
        type: 'office_action',
        label: outlineAwareLabel('Insert at cursor', plainText),
        handler: 'word.insert_at_cursor',
        args: { text: plainText },
      },
      {
        type: 'office_action',
        label: outlineAwareLabel('Append to end', plainText),
        handler: 'word.append_to_end',
        args: { text: plainText },
      },
    ]);
  };

  const emitWordTablePillsFallback = () => {
    if (host !== 'word' || deps.pillsEmittedRef.current) return;
    const effectiveSkillKey = state.doneSkillKey ?? deps.forcedSkillKey;
    if (effectiveSkillKey !== 'word_table') return;
    // Same content gate as emitWordActionPillsFallback above, which this one was
    // missing. /table now declines material that is not tabular ("No table
    // structure found in the selected text." — AIVA-8038), and a decline that
    // happens to spell an example out in pipes would otherwise be harvested here
    // into a real Insert table button that pastes the example into the document.
    // The skill prompt forbids writing that example; this is the half that does
    // not depend on the model obeying it.
    if (isNonInsertableOrFindings(state.assistantContent) || isQuestionOrDecline(state.assistantContent)) return;
    const table = extractTableData(state.assistantContent);
    if (!table || table.headers.length === 0) return;
    // One data row, padded rows, placeholder headers — a dump, not a table.
    // The skill prompt is meant to have declined it; this is the half that
    // does not depend on the model, which resamples the judgement because the
    // Conversational tier's model ignores temperature (AIVA-8038).
    if (isDegenerateTable(table)) return;
    deps.pillsEmittedRef.current = true;
    const tableArgs = { headers: table.headers, rows: table.rows };
    // Replace table is always emitted and GREYED when there is no selection,
    // matching Replace selection on the text path. Emitting it only when
    // something was selected meant the row gained and lost a button as the
    // user clicked around the document, and put Replace first only here
    // (AIVA-8038).
    emitPills(
      markPillsNeedingSelection(
        [
          {
            type: 'office_action',
            label: 'Insert table here',
            handler: 'word.insert_table',
            args: { ...tableArgs, insert_position: 'at_cursor' },
          },
          {
            type: 'office_action',
            label: 'Replace table',
            handler: 'word.insert_table',
            args: { ...tableArgs, insert_position: 'replace_selection' },
          },
          {
            type: 'office_action',
            label: 'Add table to end',
            handler: 'word.insert_table',
            args: { ...tableArgs, insert_position: 'append_to_end' },
          },
        ],
        wordDoc?.hasSelection ?? false
      )
    );
  };

  const narrowCalendarSourcesToMentioned = () => {
    // Filter calendar sources to only events the LLM actually mentioned.
    // The tool fetches a wider date window for context, but only surface
    // those cards the response discusses. Fall back to showing all if no
    // subjects matched (LLM paraphrased).
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const last = prev[idx]!;
      if (!last.calendarSources || last.calendarSources.length <= 1) return prev;
      const filtered = filterCalendarSourcesByMentions(last.calendarSources, state.assistantContent);
      if (filtered.length > 0 && filtered.length < last.calendarSources.length) {
        const updated = [...prev];
        updated[idx] = { ...last, calendarSources: filtered };
        return updated;
      }
      return prev;
    });
  };

  const enrichWordPillsText = () => {
    // If the LLM emitted Word pills before the response text streamed
    // (pill dispatch during a paused tool-call, so assistantContent was
    // empty at that point), back-fill args now that we have full content.
    if (host !== 'word' || state.assistantContent.length === 0) return;
    const plainText = state.assistantContent
      .replace(/\*\*(.*?)\*\*/gs, '$1')
      .replace(/\*(.*?)\*/gs, '$1')
      .replace(/`[^`]*`/g, '')
      .trim();
    deps.setMessages((prev) => applyWordPillsFill(prev, plainText, state.assistantContent));
  };

  /**
   * Lead-in for an approval turn that streamed no prose.
   *
   * A HITL-gated call typically goes straight from tool call to
   * `approval_required` to `paused`, so nothing is streamed and the turn ENDS
   * there. The done-time fallbacks never run (they hang off `done`, and an
   * approval pause has no `done`), leaving an empty but fully styled bubble
   * above the card — background, border and padding around nothing.
   *
   * Deliberately generic and short: the card underneath already names the tool
   * and renders the arguments, so this only has to stop the bubble reading as a
   * failed response and point at the decision.
   */
  const applyApprovalLeadIn = () => {
    if (!state.approvalRequested || state.assistantContent.length > 0) return;
    state.assistantContent = 'I need your approval before I go ahead — check the details below.';
    updateAssistant();
  };

  /**
   * Build this turn's Outlook action pills from the skill × surface table.
   *
   * Runs alongside the Word equivalents above, and for the same reason: the row
   * of buttons must not depend on the model choosing to emit it.
   *
   * This is now the only source of the Outlook row. It shipped behind
   * `VITE_OUTLOOK_FRONTEND_PILLS` while the backend emitters were still live —
   * both firing at once produced duplicate buttons — and the flag went away with
   * them, so reverting is a code change rather than a config change.
   *
   * Merged through `mergePillsIntoLastAssistant` (via the pill sink) rather than
   * set directly, so these de-duplicate by label against anything already
   * attached this turn.
   */
  const emitOutlookActionPills = () => {
    if (deps.host !== 'outlook') return;
    const pills = buildOutlookPills({
      skillKey: state.doneSkillKey,
      surface: deps.outlookSurface ?? null,
      promptText: deps.promptText,
      answerText: state.assistantContent,
      draft: state.capturedDraft,
      personaSkillKeys: resolvePersonaSkillKeys(deps.host),
      succeededTools: state.succeededTools,
      failedTools: state.failedTools,
      listEmailsResultCount: state.listEmailsResultCount,
      committable: state.committable,
    });
    if (pills.length > 0) emitPills(pills);
  };

  /**
   * Build this turn's PowerPoint action bar (spec §5; SC-02, AIVA-6753).
   *
   * Same reasoning as the Outlook bar above: the buttons come from facts held
   * when the turn ends — the reply text and the skill's declared tools — so they
   * cannot go missing because the model skipped a pill call. General route only:
   * a Deckorate turn ran Deckorate's own agent and tools, not ours.
   */
  const emitPowerPointActionPills = () => {
    if (host !== 'powerpoint' || (deps.route ?? 'general') !== 'general') return;
    const pills = buildPowerPointPills({
      allowedTools: state.doneAllowedTools,
      answerText: state.assistantContent,
      committable: state.doneCommittable,
      textShapeSelected: deps.pptTextShapeSelected,
    });
    if (pills.length === 0) return;
    deps.pillsEmittedRef.current = true;
    emitPills(pills);
  };

  const handleDoneEvent = () => {
    flushProbeIfPending();
    applyEmptyBubbleFallback();
    enrichWordPillsText();
    emitWordTablePillsFallback();
    emitWordActionPillsFallback();
    // After the text/table fallbacks: those return early for /outline (it
    // declares no text placement pill), so this cannot race them.
    void emitWordOutlinePillsFallback();
    // After the pill fallbacks: the highlights are independent of them, and the
    // review skills reach none of those paths. Not awaited — the turn is
    // finished either way, and the highlights land as they resolve.
    void highlightReviewFindings();
    emitOutlookActionPills();
    emitPowerPointActionPills();
    narrowCalendarSourcesToMentioned();
  };

  const handleFrontendToolCall = (event: SSEEvent) => {
    if (!event.data.handler || !event.data.tool_call_id) return;
    // NOTE: a duplicate ui.pill.word.insert_email from a stale skill document is
    // NOT dropped here. The queue is what produces the /chat/resume that frees
    // the suspended graph, so skipping an entry hangs the turn. The duplicate is
    // handled where it is harmless instead — dedupePills collapses pills with an
    // identical handler+label, which is exactly what a re-emission of the same
    // candidates produces.
    state.pendingQueue.push({
      toolCallId: event.data.tool_call_id,
      handler: event.data.handler,
      args: event.data.args ?? {},
      timeoutMs: event.data.timeout_ms ?? 30000,
    });
    if (event.data.handler === 'word.docreview.scan') {
      // Every scan replaces the previous scope, so a later full review never
      // inherits an earlier scoped ask's labels. The judge that follows runs
      // on a resumed stream, which is why this lives in hook state, not here.
      const rules = (event.data.args as { rules?: unknown } | undefined)?.rules;
      const keys = Array.isArray(rules) ? rules.filter((r): r is string => typeof r === 'string') : [];
      deps.setDocReviewScope?.(keys.length > 0 ? keys : null);
    }
    deps.setActiveTool(event.data.tool_name ?? event.data.handler);
    // AIVA-8199: a slow tool names itself, else the line still shows a sibling's label.
    if (event.data.progress_label) deps.setActivityLine(event.data.progress_label);
  };

  const handleDisplayPill = (event: SSEEvent) => {
    // Backend-attached "click later" pill (e.g. find_slots free-slot pills)
    // flushed after all text tokens. Render immediately via the same handler
    // dispatch as model-emitted pills, but do NOT queue — no /chat/resume
    // follows because no interrupt suspended the graph.
    if (!event.data.handler) return;
    // A guardrail-flagged turn renders as a static notice — pill emissions
    // that arrive after the flag are dropped for the rest of the turn.
    if (state.guardrailFlagged) return;
    if (event.data.handler === 'ui.pill.chat.book_slot') {
      const iso = (event.data.args as { start_iso?: string } | undefined)?.start_iso;
      if (typeof iso === 'string') state.backendBookSlotIsos.add(iso);
    }
    void runHandler(host, event.data.handler, event.data.args ?? {}, event.data.timeout_ms ?? 5000);
  };

  const handleErrorEvent = (event: SSEEvent) => {
    state.assistantContent = event.data.message ?? 'Something went wrong. Please try again.';
    updateAssistant();
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      const last = updated[idx];
      // deckSummary too (DI-01 AC-5): its header and note belong to a summary, not the error text.
      if (last?.role === 'assistant') updated[idx] = { ...last, actionPills: undefined, deckSummary: undefined };
      return updated;
    });
    deps.setActivityLine(null);
  };

  // --- Chat v2 handlers (AIVA-5929) -------------------------------------

  const handlePlanUpdate = (event: SSEEvent) => {
    // Each plan_update carries the FULL plan snapshot — replace, never merge.
    if (state.guardrailFlagged || !Array.isArray(event.data.todos)) return;
    state.plan = event.data.todos;
    updateAssistant();
  };

  const handleApprovalRequired = (event: SSEEvent) => {
    // A HITL-gated action paused the run server-side. This is NOT a frontend
    // tool call — never queue it for the resume loop. Park the request on the
    // assistant message so ApprovalCard renders, and let the turn end on the
    // `paused` terminal event that follows.
    if (!event.data.interrupt_id || !event.data.tool) return;
    state.approvalRequested = true;
    const approval: ApprovalRequest = {
      interrupt_id: event.data.interrupt_id,
      tool: event.data.tool,
      args: event.data.args ?? {},
    };
    if (typeof event.data.description === 'string' && event.data.description) {
      approval.description = event.data.description;
    }
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      const last = updated[idx];
      if (last?.role === 'assistant') updated[idx] = { ...last, approval };
      return updated;
    });
    deps.setActiveTool(null);
    deps.setActivityLine(null);
  };

  const handleMultipartPlan = (event: SSEEvent) => {
    // The backend split a multi-task request into confirmable steps. This turn
    // ran no agent and touched nothing — park the plan on the assistant message
    // so MultipartPlanCard can offer step 1, and let `done` end the turn.
    //
    // A guardrail-flagged turn drops it: that message has been replaced by the
    // static category notice, and offering to start work under it would be
    // exactly the action the screen just refused.
    if (state.guardrailFlagged) return;
    const steps = Array.isArray(event.data.steps) ? event.data.steps : [];
    // Fewer than two steps is not a plan. The backend already refuses to emit
    // one, so this only guards against a partial/garbled payload — and dropping
    // it silently is right: the turn's text still stands on its own.
    if (steps.length < 2) return;
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const updated = [...prev];
      const last = updated[idx];
      if (last?.role === 'assistant') {
        updated[idx] = { ...last, multipartPlan: { steps, cursor: 0 } };
      }
      return updated;
    });
    deps.setActiveTool(null);
    deps.setActivityLine(null);
  };

  const handleGuardrailFlagged = (event: SSEEvent) => {
    // The turn was stopped by the content-safety screen. Replace whatever
    // partial response streamed with the static category notice and suppress
    // every later token / pill emission; a `done` event follows.
    const categories = Array.isArray(event.data.categories) ? event.data.categories : [];
    // Absent on an older backend — guardrailMessage then falls back to message
    // copy, which is exactly how this read before attribution existed.
    const sources = Array.isArray(event.data.sources) ? event.data.sources : [];
    state.guardrailFlagged = true;
    state.guardrailCategories = categories;
    state.guardrailSources = sources;
    state.assistantContent = guardrailMessage(categories, sources);
    state.plan = undefined;
    updateAssistant();
    deps.setActivityLine(null);
  };

  const handleReplyReplaced = (event: SSEEvent) => {
    // AIVA-8875: the reply leaked the model's tool calls as text. The backend
    // has already saved the replacement; show the same, and drop any pills the
    // broken reply produced — they would place or copy the garble.
    state.replyReplaced = true;
    state.assistantContent =
      typeof event.data.content === 'string' && event.data.content ? event.data.content : GARBLED_REPLY_FALLBACK;
    updateAssistant();
  };

  const handleActionPills = (event: SSEEvent) => {
    if (state.replyReplaced) return;
    // End-of-turn aggregate of the pills the backend attached this turn.
    // Individual display_pill events already rendered during the turn, so
    // dedupe by label against pills already attached to the message.
    if (state.guardrailFlagged) return;
    const rawPills = Array.isArray(event.data.pills) ? event.data.pills : [];
    const mapped: Pill[] = [];
    const seenLabels = new Set<string>();
    for (const raw of rawPills) {
      const pill = mapAggregatePill(raw);
      if (pill && !seenLabels.has(pill.label)) {
        seenLabels.add(pill.label);
        mapped.push(pill);
      }
    }
    if (mapped.length === 0) return;
    deps.setMessages((prev) => {
      const idx = findAssistantIndex(prev);
      if (idx < 0) return prev;
      const m = prev[idx]!;
      const existingLabels = new Set((m.actionPills ?? []).map((p) => p.label));
      const toAdd = mapped.filter((p) => !existingLabels.has(p.label));
      if (toAdd.length === 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...m, actionPills: [...(m.actionPills ?? []), ...toAdd] };
      return updated;
    });
  };

  const commitReasoningDuration = () => {
    if (state.reasoningStartedAt === null || state.reasoningEndedAt === null) return;
    state.reasoningDurationMs = Math.max(0, state.reasoningEndedAt - state.reasoningStartedAt);
  };

  const captureThreadId = (event: SSEEvent) => {
    // done AND paused carry the server thread id; thread_reset means the
    // server discarded an unknown/expired id and started a fresh thread.
    if (typeof event.data.thread_id === 'string' && event.data.thread_id) {
      deps.onThreadIdResolved(event.data.thread_id, event.data.thread_reset === true);
    }
  };

  const captureCapabilityFacts = (event: SSEEvent) => {
    const span = deps.capabilitySpan;
    if (!span) return;
    const d = event.data;
    span.observe({
      ...(typeof d.capability_id === "string" && d.capability_id
        ? { capabilityId: d.capability_id }
        : {}),
      ...(typeof d.thread_messages === "number" ? { threadMessages: d.thread_messages } : {}),
      ...(typeof d.thread_messages_exact === "boolean"
        ? { threadMessagesExact: d.thread_messages_exact }
        : {}),
      ...(typeof d.thread_truncated === "boolean" ? { truncated: d.thread_truncated } : {}),
      ...(typeof d.hit_count === "number" ? { hitCount: d.hit_count } : {}),
      ...(typeof d.hit_total === "number" ? { hitTotal: d.hit_total } : {}),
      ...(typeof d.external_count === "number" ? { externalCount: d.external_count } : {}),
      aiUsed: true,
    });
  };

  const handlePaused = (event: SSEEvent) => {
    // Backend closed the stream waiting for /resume. The dispatch loop
    // in useChat drains the pendingQueue after the SSE reader returns
    // (approval pauses queue nothing — the turn just ends).
    commitReasoningDuration();
    flushProbeIfPending();
    applyApprovalLeadIn();
    if (state.reasoningDurationMs !== undefined) updateAssistant();
    captureThreadId(event);
    captureCapabilityFacts(event);
  };

  const handleReasoning = (event: SSEEvent) => {
    if (!event.data.content) return;
    state.reasoningStartedAt ??= Date.now();
    state.reasoningEndedAt = Date.now();
    state.reasoningContent += event.data.content;
    updateAssistant();
  };

  const handleToken = (event: SSEEvent) => {
    // A guardrail-flagged turn suppresses every later token.
    if (event.data.content && !state.guardrailFlagged) handleTokenChunk(event.data.content);
  };

  const handleDone = (event: SSEEvent) => {
    // AIVA-8792 / AIVA-9184 — a handoff turn ran nothing on v2: no text, no
    // pills, no thread. Skip the done-time fallbacks (they would fill the empty
    // bubble with "no response" copy) — useChat removes the bubble and re-runs
    // the turn on Deckorate or as the slide rewrite.
    if (state.handoffTarget) {
      deps.setActivityLine(null);
      return;
    }
    state.doneSkillKey = event.data.skill_key;
    state.doneAllowedTools = Array.isArray(event.data.allowed_tools) ? event.data.allowed_tools : undefined;
    const committable = (event.data as { committable?: unknown }).committable;
    state.doneCommittable = typeof committable === 'boolean' ? committable : undefined;
    // Only an explicit `false` suppresses the pill row. An absent field means an
    // older backend that does not send it, which must keep behaving as today.
    if (event.data.committable === false) state.committable = false;
    commitReasoningDuration();
    // Always re-render on done — not just when reasoning streamed (main's
    // AIVA-6436 fix). This is what stamps allowedTools onto the message;
    // without it a no-reasoning turn leaves msg.allowedTools undefined, so
    // buildWordDocPills' isAuthorised() takes its "we don't know" branch and
    // injects Insert/Append pills the skill never declared — /grammar shows
    // three pills instead of Replace only.
    updateAssistant();
    if (!state.guardrailFlagged) {
      // The done-time fallbacks (probe flush, empty-bubble text, Word
      // pills) never apply to a guardrail-flagged turn — its bubble is
      // the static notice and must stay pill-free.
      handleDoneEvent();
    }
    captureThreadId(event);
    captureCapabilityFacts(event);
    deps.setActivityLine(null);
  };

  // One entry per SSE event type. A table rather than a switch so each case is a
  // single named handler and the dispatcher stays flat (Sonar typescript:S3776).
  const handlers: Record<string, (event: SSEEvent) => void> = {
    tool_start: (event) => {
      if (event.data.tool) deps.setActiveTool(event.data.tool);
    },
    frontend_tool_call: handleFrontendToolCall,
    display_pill: handleDisplayPill,
    paused: handlePaused,
    tool_end: handleToolEnd,
    reasoning: handleReasoning,
    token: handleToken,
    plan_update: handlePlanUpdate,
    activity: (event) => {
      deps.setActivityLine(
        typeof event.data.label === 'string' && event.data.label ? event.data.label : null
      );
      // Also track the raw tool id behind this activity line (shared with the
      // interrupt-tool-call path's own setActiveTool calls, which already use
      // the same tool-id string space) — ChatMessages uses it to detect a
      // long-running, single opaque tool call (word_docreview_judge) and
      // rotate through a few genuinely-accurate sub-step labels instead of
      // sitting on one static line for its whole ~20-40s duration.
      deps.setActiveTool(typeof event.data.tool === 'string' && event.data.tool ? event.data.tool : null);
    },
    approval_required: handleApprovalRequired,
    guardrail_flagged: handleGuardrailFlagged,
    reply_replaced: handleReplyReplaced,
    action_pills: handleActionPills,
    multipart_plan: handleMultipartPlan,
    handoff: (event) => {
      const target = event.data.target;
      if (target === 'deckorate' || target === 'rephrase_slide') state.handoffTarget = target;
    },
    done: handleDone,
    error: handleErrorEvent,
  };

  return (event: SSEEvent) => {
    handlers[event.event]?.(event);
  };
}

/**
 * Index of the message this turn streams into, falling back to the last
 * assistant message.
 *
 * The id-only match silently attached to nothing whenever the turn's message
 * had been re-created between the interrupt and the render, so the rendered
 * artefact never appeared — the count sentence did, because that comes from
 * the model's text rather than from the attached field.
 */
function findTurnMessageIndex(messages: ChatMessage[], assistantMsgId: string): number {
  const byId = messages.findIndex((m) => m.id === assistantMsgId);
  if (byId >= 0) return byId;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]!.role === 'assistant') return i;
  }
  return -1;
}

/**
 * RV-02: the comments list is a rendered artefact, not just a value the backend
 * consumes. Attach it to this turn's message so WordCommentsCard can draw the
 * rows; the same object still returns to the graph on resume.
 */
function attachWordComments(result: Record<string, unknown>, deps: TurnDeps): void {
  // runHandler returns a ResumeResult envelope — { ok, data } — so the
  // handler's own object is under `data`. Reading result.comments directly
  // was always undefined, which is why the rows never attached however
  // carefully the field was preserved downstream.
  const payload = (result as { data?: { comments?: unknown } }).data ?? (result as { comments?: unknown });
  const rows = (payload as { comments?: unknown }).comments;
  if (!Array.isArray(rows) || rows.length === 0) return;
  deps.setMessages((prev) => {
    const idx = findTurnMessageIndex(prev, deps.assistantMsgId);
    if (idx < 0) return prev;
    const updated = [...prev];
    updated[idx] = { ...updated[idx]!, wordComments: rows as WordComment[] };
    return updated;
  });
}

/**
 * The heading outline is a rendered artefact, not just a value the backend
 * consumes — the same reasoning as attachWordComments above. Attach it to this
 * turn's message so WordOutlineCard can draw the rows; the same object still
 * returns to the graph on resume.
 *
 * This is what replaces the model transcribing the outline into the bubble. On a
 * 171-heading document that transcription truncated at #71.
 */
function attachWordOutline(result: Record<string, unknown>, deps: TurnDeps): boolean {
  // runHandler returns a ResumeResult envelope — { ok, data } — so the handler's
  // own object is under `data`. Reading result.outline directly is undefined,
  // which is the bug attachWordComments' comment records.
  const payload = (result as { data?: Record<string, unknown> }).data ?? result;
  const outline = (payload as { outline?: { rows?: unknown } }).outline;
  const health = (payload as { health?: Record<string, unknown> }).health;
  const rows = outline?.rows;
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const value: WordOutline = {
    rows: rows as WordOutline["rows"],
    total: typeof health?.total === "number" ? health.total : rows.length,
    depth: typeof health?.depth === "number" ? health.depth : 1,
    styleBacked: typeof health?.styleBacked === "number" ? health.styleBacked : 0,
    outlineLevelOnly: typeof health?.outlineLevelOnly === "number" ? health.outlineLevelOnly : 0,
    hasToc: typeof (payload as { hasToc?: unknown }).hasToc === "boolean" ? (payload as { hasToc: boolean }).hasToc : null,
  };
  deps.setMessages((prev) => {
    const idx = findTurnMessageIndex(prev, deps.assistantMsgId);
    if (idx < 0) return prev;
    const updated = [...prev];
    updated[idx] = { ...updated[idx]!, wordOutline: value };
    return updated;
  });
  return true;
}

function reattachWordOutlineRows(result: Record<string, unknown>, deps: TurnDeps): void {
  const payload = (result as { data?: Record<string, unknown> }).data ?? result;
  const rows = (payload as { outline?: { rows?: unknown } }).outline?.rows;
  if (!Array.isArray(rows) || rows.length === 0) return;
  const typed = rows as WordOutline["rows"];
  delete (payload as { outline?: unknown }).outline;
  deps.setMessages((prev) => {
    const idx = findTurnMessageIndex(prev, deps.assistantMsgId);
    if (idx < 0) return prev;
    const updated = [...prev];
    const prior = updated[idx]!.wordOutline;
    updated[idx] = {
      ...updated[idx]!,
      wordOutline: {
        styleBacked: prior?.styleBacked ?? 0,
        outlineLevelOnly: prior?.outlineLevelOnly ?? 0,
        hasToc: prior?.hasToc ?? null,
        rows: typed,
        total: typed.length,
        depth: Math.max(...typed.map((row) => row.level)),
      },
    };
    return updated;
  });
}

/**
 * Set the pillsEmittedRef sentinel and capture the first pill text, which
 * applyEmptyBubbleFallback shows when the turn streamed no text of its own
 * (e.g. a paused tool call). First text to arrive wins.
 */
function capturePillText(next: PendingToolCall, result: Record<string, unknown>, deps: TurnDeps): void {
  if (next.handler === 'ui.render_action_pills') {
    deps.pillsEmittedRef.current = true;
    const rawPills = next.args.pills;
    const pills: Pill[] = Array.isArray(rawPills) ? (rawPills as Pill[]) : [];
    const pillText = extractFirstWordPillText(pills, WORD_TEXT_PILL_HANDLERS);
    if (pillText) deps.lastWordPillTextRef.current = pillText;
    return;
  }
  if (!next.handler.startsWith('ui.pill.')) return;
  deps.pillsEmittedRef.current = true;
  if (deps.lastWordPillTextRef.current) return;
  // PowerPoint too (AIVA-8972): its pill tools render nothing, so a reply the
  // model put only in the pill call becomes the reply, and the bar is built on it.
  if ((deps.host === 'word' || deps.host === 'powerpoint') && typeof next.args.text === 'string' && next.args.text) {
    deps.lastWordPillTextRef.current = next.args.text;
    return;
  }
  // Some ui.pill.* handlers decide NOT to render a row at all — e.g. RV-04's
  // clean-document no-op ("No tracked changes in this document.") — and
  // return an explanatory message instead of pills. Without this, a turn
  // where the model streams no text of its own falls through to
  // applyEmptyBubbleFallback's generic Word text ("Ready — click a button
  // below…"), which is wrong when there is no button to click.
  const handlerData = (result as { data?: Record<string, unknown> }).data;
  if (handlerData?.rendered === false && typeof handlerData.message === 'string') {
    deps.lastWordPillTextRef.current = handlerData.message;
  }
}

/**
 * Runs one queued frontend tool call and returns the `{ ok, data }` envelope
 * the backend tool_factory expects. Every frontend tool (including pill_*
 * emission tools) dispatches the same way: runHandler resolves the handler
 * key and invokes it. The pillsEmittedRef sentinel is set when ANY
 * `ui.pill.*` handler runs, which suppresses the Word fallback /
 * empty-bubble paths.
 *
 * Shared by useChat's resume loop and the approval-continuation loop in
 * useApprovalDecision so both drain streams identically.
 */
export async function dispatchPendingToolCall(
  state: TurnState,
  deps: TurnDeps,
  next: PendingToolCall
): Promise<Record<string, unknown>> {
  // Dedupe: when the backend attached book-slot pills this turn (a fresh
  // find_slots presentation turn), those ARE the only pills that belong.
  // Suppress every model-emitted pill_chat_meeting_followup — the model
  // keeps recreating book pills under improvised labels ("Choose a time",
  // "Book 1:00 PM Tue", "…for 2026-06-30 at 1:00 PM") that no single
  // pattern catches. Still return a result so the interrupted graph
  // resumes (else it hangs). This is safe because backend book pills only
  // exist on a fresh-booking discovery turn — reschedule / view turns
  // attach none (backend suppresses them), so their Case 5-8 follow-ups
  // render normally.
  if (next.handler === 'ui.pill.chat.meeting_followup' && state.backendBookSlotIsos.size > 0) {
    deps.pillsEmittedRef.current = true;
    return { ok: true, data: { rendered: true, deduped: true } };
  }
  const result = (await runHandler(
    deps.host,
    next.handler,
    next.args,
    next.timeoutMs,
    // AIVA-7506: only word.docreview.scan actually reads ctx today (to POST
    // its deterministic findings straight to the bulk-create endpoint) —
    // every other handler ignores it, same as Deckorate's own handlers do
    // when a plain Office.js call has no need for it.
    { apiToken: deps.apiToken, refresh: deps.refresh, threadId: deps.threadIdRef.current, signal: deps.signal },
  )) as unknown as Record<string, unknown>;
  if (next.handler === "word.list_comments") attachWordComments(result, deps);
  if (next.handler === "word.get_document_structure" && attachWordOutline(result, deps)) {
    state.wordOutlineRendered = true;
  }
  if (next.handler === "ui.pill.word.insert_outline") reattachWordOutlineRows(result, deps);
  // AIVA-7506: word_docreview_anchor_findings is the last frontend-dispatched
  // step of the check-references flow (word_docreview_judge and
  // word_docreview_confirm_anchors that bracket it are plain backend tools
  // with no frontend dispatch at all) — there is no later frontend-visible
  // event to key off instead. A ref flag rather than a per-message one: the
  // token-streaming that follows in the same turn may replace rather than
  // merge the message object, which would silently drop a per-message flag.
  if (next.handler === "word.docreview.anchor_findings") {
    deps.markDocReviewReady();
  }
  capturePillText(next, result, deps);
  return result;
}
